const DEFAULT_FIELD_LABELS: Record<string, string> = {
    email: 'email address',
    phone: 'phone number',
    phone_primary: 'primary phone number',
    payment_plan_id: 'payment plan',
    property_id: 'unit',
    reservation_id: 'reservation',
    lead_id: 'lead',
    lead_ids: 'selected leads',
    fast_reservation_session_id: 'verification session',
    session_id: 'verification session',
    file_id: 'uploaded file',
    id_number: 'ID / passport number',
    full_name: 'full name',
    arabic_full_name: 'Arabic full name',
    nationality: 'nationality',
    birth_date: 'birth date',
    address_line1: 'address line 1',
    address_line2: 'address line 2',
    address_city: 'city',
    address_country: 'country',
    confirmed_data: 'reservation details',
    amount: 'reservation amount',
    channel: 'verification method',
    code: 'verification code'
};

const DEFAULT_CODE_MESSAGES: Record<string, string> = {
    // --- Validation errors (REQUEST_INVALID_*) ---
    REQUEST_INVALID_EMAIL: 'Enter a valid email address.',
    REQUEST_INVALID_PHONE: 'Enter a valid phone number with country code.',
    REQUEST_INVALID_EMAIL_OR_PHONE: 'Enter a valid email address or phone number.',
    REQUEST_INVALID_PASSWORD: 'Use at least 8 characters with letters and numbers.',
    REQUEST_INVALID_CHANNEL: 'Choose whether to verify by email or phone.',
    REQUEST_INVALID_CODE: 'Enter the 6-digit verification code.',
    REQUEST_INVALID_PAYMENT_PLAN_ID: 'Choose a valid payment plan for this unit.',
    REQUEST_INVALID_FILE_TYPE: 'Upload an image or PDF file.',
    REQUEST_INVALID_FILE_ID: 'The selected file is no longer available. Please upload it again.',
    REQUEST_INVALID_CONFIRMED_DATA: 'Review the reservation details and correct any invalid fields.',
    REQUEST_INVALID_FAST_RESERVATION_SESSION_ID: 'Your verification session expired. Start again.',
    REQUEST_INVALID_RESERVATION_ID: 'This reservation reference is invalid. Refresh and try again.',
    REQUEST_INVALID_PROPERTY_ID: 'This unit reference is invalid. Refresh and try again.',
    REQUEST_INVALID_LEAD_ID: 'This lead reference is invalid. Refresh and try again.',
    REQUEST_INVALID_LEAD_IDS: 'One or more selected leads are invalid. Refresh and try again.',
    REQUEST_INVALID_AMOUNT: 'Enter a valid reservation amount.',
    REQUEST_INVALID_PASSWORD_EMAIL_REQUIRED: 'Add a valid email address before setting a password.',
    REQUEST_NO_CHANGES: 'No changes to save.',

    // --- Auth / permission errors ---
    AUTHZ_FORBIDDEN: "You don't have permission to perform this action. Please contact your administrator.",
    AUTHZ_ORG_REQUIRED: 'You need to be signed in under an organization to perform this action.',
    AUTHZ_ORG_MISMATCH: 'Your session doesn\'t match the requested organization. Please sign in again.',
    AUTHZ_ROLE_REQUIRED: "Your account role doesn't allow this action. Please contact your administrator.",
    AUTHZ_NO_ACTIVE_MEMBERSHIP: "Your account doesn't have an active membership in this organization. Please contact your administrator.",
    AUTH_INVALID_TOKEN: 'Your session has expired. Please sign in again.',
    AUTHZ_PERM_VER_MISMATCH: 'Your permissions have changed. Please sign in again to continue.',

    // --- Not-found / resource errors ---
    NOT_FOUND: 'The requested item was not found. It may have been deleted or you may not have access.',
    FAST_RESERVATION_SESSION_NOT_FOUND: 'Your verification session expired. Start again.',
    DELETED_LEAD_RECREATION_BLOCKED: 'This lead was previously deleted and cannot be recreated.',

    // --- Offer errors ---
    OFFER_LOCKED: 'This offer has been sent and can no longer be edited.',
    OFFER_REVIEW_REQUIRED: 'Review and confirm the offer before sending.',
    OFFER_PDF_NOT_READY: 'The offer PDF is being generated. Please try again in a moment.',
    OFFER_APPENDIX_REQUIRED: 'This offer requires an appendix PDF before proceeding.',
    OFFER_APPENDIX_UNAVAILABLE: "The offer appendix couldn't be generated. Please check the offer settings or try again.",

    // --- Reservation / property errors ---
    PROPERTY_NOT_AVAILABLE: 'This unit is no longer available for reservation.',
    HOLD_NOT_ACTIVE: 'This hold is no longer active.',
    RESERVATIONS_DISABLED: 'Reservations are not currently enabled for this property or project.',
    RESERVATION_FAILED: 'We couldn\'t complete the reservation. Please try again or contact support.',
    INVALID_STATE: "This action can't be performed in the current state. Please refresh and try again.",
    IDENTITY_FIELDS_REQUIRED: 'Please complete all required identity fields before continuing.',
    PROJECT_ACTION_NOT_ELIGIBLE: "You're not eligible to perform this action. Contact your admin.",

    // --- Hold & extension workflow (shared codes first, extension-specific after) ---
    APPROVAL_CLOSED: 'This approval request has already been decided.',
    APPROVAL_COMMENT_REQUIRED: 'A comment is required when declining.',
    APPROVAL_NOT_ASSIGNED: 'You are not assigned to this approval request.',
    HOLD_EXTENSION_NOT_CONFIGURED: 'Extension requests are not enabled for this project.',
    HOLD_EXTENSION_WINDOW_NOT_OPEN: 'Extensions can only be requested in the final hours before the hold expires.',
    HOLD_EXTENSION_REQUEST_EXISTS: 'An extension request for this unit is already awaiting a decision.',
    HOLD_EXTENSION_LIMIT_REACHED: 'This hold already used its one allowed extension.',
    HOLD_EXTENSION_NOT_ALLOWED: 'This reservation is not eligible for a hold extension.',
    HOLD_EXTENSION_APPROVAL_REQUIRED: 'Extending this hold requires approval. Submit an extension request instead.',
    HOLD_EXTENSION_EXPIRED: 'The extension request expired before a decision was made.',
    HOLD_APPROVERS_REQUIRED: 'Select at least one approver when the hold workflow is enabled.',
    HOLD_APPROVER_INVALID: 'One or more selected approvers are not valid members of this organization.',
    REQUEST_INVALID_HOLD_REQUEST_ID: 'This extension request reference is invalid. Refresh and try again.',
    REQUEST_INVALID_HOLD_DAYS: 'Hold days must be between 1 and 30.',
    REQUEST_INVALID_EXTENSION_HOURS: 'Extension hours must be between 1 and 72.',
    REQUEST_INVALID_DECISION: 'The decision value is invalid. Please refresh and try again.',

    // --- File / storage errors ---
    REQUEST_TOO_LARGE: 'The uploaded file is too large. Please use a smaller file.',
    REQUEST_INVALID_MULTIPART: 'The upload format is invalid. Please try again.',
    FILE_STREAM_FAILED: 'The file couldn\'t be loaded. Please try again.',
    STORAGE_UNAVAILABLE: 'File storage is temporarily unavailable. Please try again later.',

    // --- Email / signing errors ---
    EMAIL_UNAVAILABLE: 'We couldn\'t send the email right now. Please try again later.',
    EMAIL_INVALID: 'The email address is invalid. Please check and try again.',
    ZOHO_SIGN_ERROR: 'Electronic signing is temporarily unavailable. Please try again later or contact support.',
    ZOHO_OAUTH_FAILED: 'Electronic signing is temporarily unavailable. Please try again later.',

    // --- Workspace / partner errors ---
    COMPANY_WORKSPACE_DELETE_CONFIRMATION_REQUIRED: 'Type the workspace name to confirm deletion.',
    REQUEST_INVALID_WORKSPACE: 'This workspace reference is invalid. Refresh and try again.',
    REQUEST_INVALID_PERMISSION_CODE: 'One or more selected permissions are invalid.',

    // --- System / config errors ---
    CONFIG_MISSING: 'This feature isn\'t configured yet. Please contact support.',
    DB_MIGRATION_REQUIRED: 'A system update is required. Please contact support.',
    IDEMPOTENCY_KEY_REQUIRED: 'This request requires a unique identifier. Please refresh and try again.',
    IDEMPOTENCY_KEY_REUSED: 'This request was already submitted. Please refresh and try again.',
    IDEMPOTENCY_FAILED: 'We couldn\'t process your request. Please try again.',
    PROD_TO_DEV_SYNC_RUNNING: 'A sync operation is already running. Please wait for it to finish.',
    SYNC_PARITY_CHECK_FAILED: 'A data sync check failed. Please contact support.',
    REQUEST_INVALID_SYSTEM_FEATURE: 'The feature flag value is invalid.',
    REQUEST_INVALID_DATE_RANGE: 'The date range is invalid. Please check the selected dates.',
    REQUEST_INVALID_COMMISSION: 'Commission must be between 0 and 100.',
    REQUEST_INVALID_COUNTRY: 'The country code is invalid. Use a 2-letter code (e.g. EG).',
    REQUEST_INVALID_CURRENCY: 'The currency code is invalid. Use a 3-letter code (e.g. EGP).',
    REQUEST_INVALID_FILE: 'The file is invalid or unsupported. Please upload an .xlsx or .csv file.',
};

type ApiErrorFormatOptions = {
    fallback: string;
    heading?: string | null;
    fieldLabels?: Record<string, string>;
};

function firstNonEmptyString(...values: unknown[]): string | null {
    for (const value of values) {
        const text = String(value ?? '').trim();
        if (text) return text;
    }
    return null;
}

function errorPayload(error: any): any {
    return error?.error?.error ?? error?.error ?? error ?? {};
}

function fieldLabel(field: string | null, customLabels?: Record<string, string>): string | null {
    const raw = String(field || '').trim();
    if (!raw) return null;
    const key = raw.toLowerCase();
    const label = customLabels?.[key] || DEFAULT_FIELD_LABELS[key];
    if (label) return label;
    return raw
        .replace(/\[\d+\]/g, '')
        .replace(/[._-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function bulletList(items: any[], customLabels?: Record<string, string>): string[] {
    return items
        .map((item: any) => {
            const label =
                firstNonEmptyString(
                    item?.label,
                    fieldLabel(item?.field || item?.field_name || item?.fieldName || item?.field_key || item?.fieldKey || item?.key, customLabels),
                    item?.key
                ) || '';
            const details = firstNonEmptyString(item?.details, item?.message, item?.reason);
            if (!label) return '';
            return details ? `• ${label}: ${details}` : `• ${label}`;
        })
        .filter(Boolean);
}

export function formatApiErrorMessage(error: any, options: ApiErrorFormatOptions): string {
    const payload = errorPayload(error);
    const details = payload?.details ?? null;
    const code = firstNonEmptyString(payload?.code, error?.code) || '';
    const heading = firstNonEmptyString(options.heading, options.fallback);

    const summaryLines = Array.isArray(details?.summary_lines)
        ? details.summary_lines.map((line: any) => String(line || '').trim()).filter(Boolean)
        : [];
    if (summaryLines.length) return summaryLines.join('\n');

    const missingItems = Array.isArray(details?.items)
        ? details.items
        : Array.isArray(details?.missing_items)
            ? details.missing_items
            : Array.isArray(details?.errors)
                ? details.errors
                : [];
    const bullets = bulletList(missingItems, options.fieldLabels);
    if (bullets.length) return `${heading}\n${bullets.join('\n')}`;

    const missingFields = Array.isArray(details?.missing)
        ? details.missing
        : Array.isArray(details?.missing_fields)
            ? details.missing_fields
            : [];
    if (missingFields.length) {
        const labels = missingFields
            .map((field: unknown) => fieldLabel(String(field || ''), options.fieldLabels))
            .filter(Boolean);
        if (labels.length) return `${heading}: ${labels.join(', ')}.`;
    }

    const explicitField = fieldLabel(
        firstNonEmptyString(
            payload?.field,
            payload?.field_name,
            payload?.fieldName,
            details?.field,
            details?.field_name,
            details?.fieldName,
            details?.field_key,
            details?.fieldKey
        ),
        options.fieldLabels
    );
    if (code && DEFAULT_CODE_MESSAGES[code]) {
        return DEFAULT_CODE_MESSAGES[code];
    }
    if (code.startsWith('REQUEST_INVALID_') && explicitField) {
        return `Please check ${explicitField}.`;
    }

    return (
        firstNonEmptyString(payload?.message, error?.message, options.fallback)
        || options.fallback
    );
}

/**
 * Lightweight code→message lookup for interceptors and non-component code.
 * Returns the friendly text for a known error code, or null if unmapped.
 */
export function friendlyMessageForCode(code: string | null | undefined): string | null {
    if (!code) return null;
    const key = String(code).trim();
    return DEFAULT_CODE_MESSAGES[key] || null;
}
